import React, { useCallback, useEffect, useRef, useState } from "react";
import TicketHandling from "./TicketHandling";
import SoundNotifications from "./SoundNotifications";
import SyncSettings from "./SyncSettings";
import PrintingSettings from "./PrintingSettings";
import { SettingsProvider, useSettingsRegistry } from "./SettingsContext";
import "./SettingsModal.css";

function SettingsModalBody({ stationId, onClose }) {
  const modalRef = useRef(null);
  const [activeTab, setActiveTab] = useState("appearance");
  const registry = useSettingsRegistry();

  const tabs = [
    { key: "appearance", label: "Appearance" },
    { key: "coursing", label: "Coursing" },
    { key: "syncStatus", label: "Sync & Status" },
    { key: "printing", label: "Printing" },
    { key: "sound", label: "Sound Notifications" },
    { key: "ticketFlow", label: "Ticket Flow" },
    { key: "admin", label: "Admin & Utilities" },
  ];

  const buildChangePrompt = (changes, title) => {
    if (!changes.length) return "";
    return `${title}\n\n` + changes.map((c) => `- ${c}`).join("\n");
  };

  const handleSave = useCallback(async () => {
    const changes = registry.getAllChanges();
    if (!changes.length) {
      alert("No changes to save.");
      return;
    }

    const ok = window.confirm(
      buildChangePrompt(changes, "Are you sure you want to save these changes?")
    );
    if (!ok) return;

    let shouldRefresh = false;

    try {
      registry.setLoading(true);
      await registry.saveAll();
      shouldRefresh = true;
      alert("Settings saved successfully.");
    } catch (err) {
      console.error("Failed to save settings:", err);
      alert(err?.message || "Error saving settings.");
    } finally {
      registry.setLoading(false);
    }

    if (shouldRefresh) {
      window.location.reload();
    }
  }, [registry]);

  const handleClose = useCallback(() => {
    const changes = registry.getAllChanges();
    if (changes.length) {
      const ok = window.confirm(
        buildChangePrompt(
          changes,
          "You have unsaved changes. Close without saving?"
        )
      );
      if (!ok) return;
    }
    if (typeof onClose === "function") {
      onClose();
    } else if (window.history.length > 1) {
      window.history.back();
    }
  }, [registry, onClose]);

  useEffect(() => {
    function handleOutsideClick(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [handleClose]);

  return (
    <>
      {registry.loading && (
        <div
          className="settings-loading-overlay"
          aria-busy="true"
          aria-live="polite"
        >
          <div className="settings-loading-card">
            {registry.loadingMessage || "Saving settings..."}
          </div>
        </div>
      )}
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black bg-opacity-40 px-4 py-4">
        <div
          ref={modalRef}
          className="flex max-h-[calc(100vh-2rem)] w-[1240px] max-w-[96vw] flex-col rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-800"
        >
          <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col">
            <h2 className="mb-4 text-center text-2xl font-bold text-gray-900 dark:text-white">
              KDS Settings
            </h2>

            <div className="mb-6 flex flex-wrap justify-center gap-6 border-b pb-0 dark:border-gray-700">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`border-b-2 px-1 py-3 text-sm font-semibold transition ${
                    activeTab === tab.key
                      ? "border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400"
                      : "border-transparent text-gray-700 hover:text-blue-600 dark:text-gray-200 dark:hover:text-blue-400"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mx-auto min-h-0 max-w-4xl flex-1 overflow-y-auto">
              <div
                className={`${
                  activeTab === "appearance" ? "block" : "hidden"
                } space-y-6`}
              >
                <TicketHandling
                  stationId={stationId}
                  forcedSubTab="display"
                  hideSubTabBar
                />
              </div>

              <div
                className={`${
                  activeTab === "coursing" ? "block" : "hidden"
                } space-y-6`}
              >
                <TicketHandling
                  stationId={stationId}
                  forcedSubTab="coursing"
                  hideSubTabBar
                  sectionMode="coursing"
                />
                <TicketHandling
                  stationId={stationId}
                  forcedSubTab="categories"
                  hideSubTabBar
                />
              </div>

              <div className={activeTab === "syncStatus" ? "block" : "hidden"}>
                <TicketHandling
                  stationId={stationId}
                  forcedSubTab="coursing"
                  hideSubTabBar
                  sectionMode="syncStatus"
                />
              </div>

              <div className={activeTab === "sound" ? "block" : "hidden"}>
                <SoundNotifications stationId={stationId} />
              </div>

              <div className={activeTab === "printing" ? "block" : "hidden"}>
                <PrintingSettings stationId={stationId} />
              </div>

              <div
                className={`${
                  activeTab === "ticketFlow" ? "block" : "hidden"
                } space-y-6`}
              >
                <TicketHandling
                  stationId={stationId}
                  forcedSubTab="behavior"
                  hideSubTabBar
                />
              </div>

              <div
                className={`${
                  activeTab === "admin" ? "block" : "hidden"
                } space-y-6`}
              >
                <TicketHandling
                  stationId={stationId}
                  forcedSubTab="admin"
                  hideSubTabBar
                />
                <SyncSettings stationId={stationId} />
              </div>
            </div>

            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={handleSave}
                className="min-w-[140px] rounded-xl bg-blue-600 px-6 py-3 text-base font-semibold text-white hover:bg-blue-700"
              >
                Save
              </button>
              <button
                onClick={handleClose}
                className="min-w-[140px] rounded-xl bg-gray-600 px-6 py-3 text-base font-semibold text-white hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function SettingsModal({ stationId, onClose }) {
  return (
    <SettingsProvider>
      <SettingsModalBody stationId={stationId} onClose={onClose} />
    </SettingsProvider>
  );
}
